import * as angular from 'angular';
import {
  debounce,
  findIndex,
  has,
  isNumber,
  isString,
  isUndefined,
  once,
  uniqueId
} from 'lodash-es';
import { Subject } from 'rxjs';

// This directive will track the scrollTop position of an element using the native History API
// Can be used on multiple directives with NO NEED for a hardcoded ID.

/**
 * @Example use cases:
 * @use-case: add scroll history directly to an element
 * <div apt-scroll-history is-content-ready="$ctrl.isGridReady" />
 *
 * @use-case: add scroll history to dynamically generated child or grand child element .e.g.(ui-grid viewport)
 * <div apt-scroll-history is-content-ready="$ctrl.isReady" scroll-history-child-selector="'.ui-grid-viewport'" />
 *
 * @use-case: fix scroll history for body - browsers generally handle this natively but Chrome wants a set body height
 * <div apt-scroll-history is-content-ready="$ctrl.isReady" bind-to-window />
 * @NOTE: bind-to-window does not need a value - presence of attribute implies truthy value
 */

/** @attribute-options
 * @is-content-ready: Rx.ReplaySubject - currently this must be an RX.ReplaySubject variable because we are not watching bindings
 *
 * @scroll-history-child-selector: string - OPTIONAL - this attribute is only necessary if you need to target an element
 * that is dynamically generated - generally a 3rd party library element
 *
 * @bind-to-window: implied truthy when present - OPTIONAL - this is only necessary if you need to bind to the Window scroll bar
 * there is a bug with Chrome not implementing the native scroll history properly if a fixed content height is not set
 * also, JQuery refuses to bind to the scroll event on the body and html tags
 */

// @NOTE: May be worth creating a general purpose service to add history state data for just about anything

export class AptScrollHistoryCtrl {
  constructor(
    private $scope,
    private $element,
    private $attrs,
    private $window,
    private $timeout
  ) {
    'ngInject';
  }

  bindToWindow: boolean;

  scrollHistoryChildSelector: string;

  // a map object of all scrollHistory entries in the history state object
  // we need to get the latest history state object every time
  get currentScrollHistoryStateMap() {
    const currentState = this.$window.history.state || {};
    return currentState.scrollHistoryMap || new Map();
  }

  // On the first call - this getter will generate
  // the scrollHistoryMapEntryId to be used
  // to retrieve the entry for this element from the scrollHistoryMap
  // residing in the history state object
  get scrollHistoryMapEntryId(): string {
    // this is a one time computation - the same value will be returned thereafter
    return once(() => {
      const allHistoryDirectiveElements = angular.element(
        '[apt-scroll-history]'
      );

      // Generate an attribute with a unique Id so we can figure out our elements
      // index relative to other elements that are using this directive.
      const currentElementUniqueLookUpId = uniqueId('historyElementId');
      // const historyElementAttrSelector = `[history-element-id="${currentElementUniqueLookUpId}"]`;
      const uniqueIdAttrName = 'historyElementId';

      // set the historyElementId attribute so we can uniquely identify this element
      // so that we can get its index
      this.$element.attr(uniqueIdAttrName, currentElementUniqueLookUpId);

      // Use the historyElementId to look up the element and get the index of the element
      // relative to other elements using this directive
      const currentElementIndex = findIndex(
        allHistoryDirectiveElements,
        (el) => {
          return (
            angular.element(el).attr(uniqueIdAttrName) ===
            currentElementUniqueLookUpId
          );
        }
      );

      // assert that our currentElementIndex is a number
      console.assert(
        isNumber(currentElementIndex),
        'currentElementIndex could not be computed - historyElementId lookup may have failed'
      );

      return `scrollHistoryItem${currentElementIndex}`;
    })();
  }

  // use getter to compute  the element we want to target for binding and scrolling
  // because we Never want to actually hold on to element reference
  get elementToScroll() {
    let elementToScroll;

    if (this.bindToWindow) {
      // bind directly to the scroll event on the window object
      elementToScroll = angular.element(window);
    } else {
      // element to scroll can be derived from the scrollHistoryChildSelector provided
      // or could be the immediate element using this directive
      elementToScroll = this.scrollHistoryChildSelector
        ? angular.element(this.scrollHistoryChildSelector, this.$element)
        : angular.element(this.$element);
    }

    return elementToScroll;
  }

  // get the scrollHistory entry for this element by using the scrollHistoryMapEntryId
  getScrollHistoryMapEntry(scrollHistoryMapEntryId) {
    if (this.currentScrollHistoryStateMap.has(scrollHistoryMapEntryId)) {
      return this.currentScrollHistoryStateMap.get(scrollHistoryMapEntryId);
    } else {
      return;
    }
  }

  // get the scrollTop position for this element from the scrollHistory entry
  getScrollTopFromScrollHistory(scrollHistoryMapEntryId): number {
    const historyEntry = this.getScrollHistoryMapEntry(scrollHistoryMapEntryId);
    if (historyEntry) {
      return historyEntry.scrollTop;
    } else {
      return 0;
    }
  }

  updateScrollFromHistoryState(
    elementToScroll,
    scrollTopFromHistoryState,
    contentReadyObservable
  ) {
    // Scroll element with Async content
    if (contentReadyObservable) {
      // contentReadyObservable
      // subscribe to the observable
      contentReadyObservable.subscribe((isReady) => {
        if (isReady) {
          // this timeout mostly helps with cases such as ui-grid
          this.$timeout(() => {
            if (scrollTopFromHistoryState) {
              // apply the scrolling
              elementToScroll.scrollTop(scrollTopFromHistoryState);
            }
          }, 10);
        }
      });
    } else {
      // Scroll element NOT requiring an Async callback
      if (scrollTopFromHistoryState) {
        // apply the scrolling
        elementToScroll.scrollTop(scrollTopFromHistoryState);
      }
    }
  }

  // this is called by angular once the binding of this element and that of it's children are ready
  // postLink does NOT take into account elements derived from a loaded template
  $postLink() {
    // this directive does not actually have watchable bindings
    // so we have to to do a one time $scope.eval to get the initial value of the expressions
    // the reason for no Isolate bindings is to avoid the multidirective error where two directives on an element
    // are trying to request an isolate scope BUT we also DO NO want to dig into the scope object
    this.scrollHistoryChildSelector = this.$scope.$eval(
      this.$attrs.scrollHistoryChildSelector
    );

    // We will subscribe to this observable further down the line but it is OK because this an ReplaySubject
    // It will immediately provide the latest value to new/late subscribers whereas Subject does NOT.
    const contentReadyObservable: Subject<boolean> = this.$scope.$eval(
      this.$attrs.isContentReady
    );

    // check if the bind-to-window attribute is present
    this.bindToWindow = has(this.$attrs, 'bindToWindow');

    // if defined - assert that scrollHistoryChildSelector is a string
    console.assert(
      isUndefined(this.scrollHistoryChildSelector) ||
        isString(this.scrollHistoryChildSelector),
      'scrollHistoryChildSelector must be of string type',
      this.scrollHistoryChildSelector
    );

    // apply a very short arbitrary timeout to clear the thread and allow element rendering to catch up
    // this timeout is especially helpful when using the scrollHistoryChildSelector
    this.$timeout(() => {
      const scrollHistoryMapEntryId: string = this.scrollHistoryMapEntryId;
      const scrollTopFromHistoryState: number = this.getScrollTopFromScrollHistory(
        scrollHistoryMapEntryId
      );

      // assert that we actually have an element to be scrolled
      console.assert(
        !!this.elementToScroll.length,
        'elementToScroll not found - ',
        this.scrollHistoryChildSelector
      );

      /*
       * Do a ONE time Update of the scrollTop position once content is ready
       * */
      this.updateScrollFromHistoryState(
        this.elementToScroll,
        scrollTopFromHistoryState,
        contentReadyObservable
      );

      /*
       * Bind to scroll event so we can update the history state object
       * */
      this.elementToScroll.on(
        'scroll',
        debounce((event) => {
          const scrollHistoryMap = this.currentScrollHistoryStateMap;
          const scrollTop = angular.element(event.target).scrollTop();

          scrollHistoryMap.set(scrollHistoryMapEntryId, { scrollTop });

          // update the history state object
          this.$window.history.replaceState(
            {
              scrollHistoryMap
            },
            ''
          );
        }, 200)
      );
    }, 10);
  }

  $onDestroy() {
    // remove the scroll event binding so that we don't end up with multiple bindings
    this.elementToScroll.off('scroll');
  }
}

export function AptScrollHistoryDirective() {
  'ngInject';
  return {
    restrict: 'A',
    scope: false, // we DO NOT want an isolate scope
    bindToController: true,
    controller: AptScrollHistoryCtrl
  };
}
